import { useEffect, useMemo, useRef, useState } from "react";
import type { ChatItem } from "./types";
import { EMOJI_CATEGORIES, formatFileSize } from "./types";
import { uploadChatAttachment } from "./uploadAttachment";

interface ChatPanelProps {
  messages: ChatItem[];
  onSend: (text: string) => void;
  onSendAttachment: (attachment: import("./types").ChatAttachment) => void;
  onReact: (messageId: string, emoji: string) => void;
  onTyping: (isTyping: boolean) => void;
  typingUsers: string[];
  onClose: () => void;
  isHost: boolean;
  title: string;
}

/* ---------- Emoji picker ---------- */
function EmojiPicker({ onPick, onClose }: { onPick: (e: string) => void; onClose: () => void }) {
  const [cat, setCat] = useState(0);
  const current = EMOJI_CATEGORIES[cat];

  return (
    <div className="absolute bottom-full left-0 mb-2 w-72 max-h-80 bg-background-800 border border-background-700 rounded-xl shadow-2xl z-30 flex flex-col overflow-hidden">
      <div className="flex items-center gap-1 p-2 border-b border-background-700 shrink-0 overflow-x-auto">
        {EMOJI_CATEGORIES.map((c, i) => (
          <button
            key={c.label}
            type="button"
            onClick={() => setCat(i)}
            className={`px-2.5 py-1.5 rounded-full text-[11px] whitespace-nowrap transition-colors ${
              i === cat ? "bg-primary-500 text-white" : "text-background-400 hover:bg-background-700"
            }`}
          >
            {c.emojis[0]} {c.label}
          </button>
        ))}
        <button
          type="button"
          onClick={onClose}
          className="ml-auto w-7 h-7 flex items-center justify-center rounded-full text-background-400 hover:bg-background-700 shrink-0"
          aria-label="Emoji seçiciyi kapat"
        >
          <i className="ri-close-line" aria-hidden="true"></i>
        </button>
      </div>
      <div className="p-2 grid grid-cols-8 gap-0.5 overflow-y-auto">
        {current.emojis.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => onPick(e)}
            className="w-8 h-8 flex items-center justify-center text-xl rounded-md hover:bg-background-700 transition-colors"
            aria-label={`Emoji ${e}`}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Image preview modal ---------- */
function ImagePreview({ url, name, onClose }: { url: string; name: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div className="max-w-4xl max-h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
        <img
          src={url}
          alt={name}
          className="max-w-full max-h-[80vh] object-contain rounded-lg"
        />
        <div className="flex items-center justify-between mt-3">
          <p className="text-sm text-background-200 truncate max-w-[60%]">{name}</p>
          <div className="flex items-center gap-2">
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="h-9 px-4 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors flex items-center gap-1.5 whitespace-nowrap"
            >
              <i className="ri-external-link-line" aria-hidden="true"></i>
              Yeni Sekmede Aç
            </a>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-700 text-background-200 hover:bg-background-600 transition-colors"
              aria-label="Kapat"
            >
              <i className="ri-close-line" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Reaction bar (hover) ---------- */
const QUICK_REACTIONS = ["👍", "❤️", "🎉", "👏", "🔥"];

/* ---------- Main panel ---------- */
export default function ChatPanel({
  messages,
  onSend,
  onSendAttachment,
  onReact,
  onTyping,
  typingUsers,
  onClose,
  isHost,
  title,
}: ChatPanelProps) {
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [hoverReaction, setHoverReaction] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, typingUsers]);

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    onSend(t);
    setText("");
    setShowEmoji(false);
    stopTyping();
  };

  const handleTextChange = (val: string) => {
    setText(val);
    if (val.trim()) {
      // Debounce "yazıyor" sinyali
      onTyping(true);
      if (typingTimer.current) clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => onTyping(false), 2500);
    } else {
      stopTyping();
    }
  };

  const stopTyping = () => {
    onTyping(false);
    if (typingTimer.current) clearTimeout(typingTimer.current);
  };

  const handleFilePick = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    setUploadError(null);
    setUploading(true);
    setUploadName(file.name);
    try {
      const attachment = await uploadChatAttachment(file);
      onSendAttachment(attachment);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Dosya yüklenemedi.");
    } finally {
      setUploading(false);
      setUploadName("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const pickEmoji = (emoji: string) => {
    setText((prev) => prev + emoji);
  };

  // Sohbeti günlere göre grupla (basit: art arda gelen aynı gönderen için tek isim bandı)
  const rendered = useMemo(() => {
    return messages.map((m, i) => {
      const prev = messages[i - 1];
      const showHeader = !prev || prev.mine !== m.mine || prev.name !== m.name;
      return { m, showHeader };
    });
  }, [messages]);

  const typingLabel = typingUsers.length > 0 ? typingUsers.join(", ") : "";

  /* Sohbeti öğretmenin kendi bilgisayarına indir */
  const [saved, setSaved] = useState(false);
  const handleDownload = () => {
    if (messages.length === 0) return;
    const now = new Date();
    const dateStr = now.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
    const timeStr = now.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
    const lines: string[] = [];
    lines.push("CANLI DERS SOHBET KAYDI");
    lines.push(`Ders: ${title}`);
    lines.push(`Kayıt Tarihi: ${dateStr} ${timeStr}`);
    lines.push("=".repeat(44));
    lines.push("");
    messages.forEach((m) => {
      let line = `[${m.time}] ${m.name}: `;
      if (m.text) line += m.text;
      if (m.attachment) {
        line += m.text ? " " : "";
        line += m.attachment.kind === "image" ? `[Resim: ${m.attachment.name}]` : `[Dosya: ${m.attachment.name}]`;
      }
      if (m.reaction) line += `  ${m.reaction}`;
      lines.push(line);
    });
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeTitle = title.replace(/[^a-zA-Z0-9çÇğĞıİöÖşŞüÜ -]/g, "").trim().replace(/\s+/g, "-") || "ders";
    a.download = `sohbet-kaydi-${safeTitle}-${now.toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="w-full sm:w-[360px] shrink-0 border-l border-background-700/50 bg-background-900 flex flex-col">
      {/* Header */}
      <div className="h-12 px-4 flex items-center justify-between border-b border-background-700/50 shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-background-100">Sohbet</h3>
          {isHost && (
            <span className="px-1.5 py-0.5 rounded bg-primary-500/20 text-primary-400 text-[10px] font-semibold uppercase tracking-wide">
              Öğretmen
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {isHost && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={messages.length === 0}
              className={`h-8 px-3 flex items-center gap-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${
                saved
                  ? "bg-green-500/15 text-green-400"
                  : "bg-background-700 text-background-200 hover:bg-background-600 disabled:opacity-40"
              }`}
              aria-label="Sohbeti kaydet"
              title="Sohbeti bilgisayarına indir"
            >
              <i className={saved ? "ri-check-line" : "ri-download-2-line"} aria-hidden="true"></i>
              {saved ? "Kaydedildi" : "Kaydet"}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-background-300 hover:bg-background-700 transition-colors"
            aria-label="Sohbeti kapat"
          >
            <i className="ri-close-line" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* Teacher-only notice */}
      {isHost && (
        <div className="mx-3 mt-3 px-3 py-2 rounded-lg bg-primary-500/10 border border-primary-500/20 shrink-0">
          <p className="text-[11px] text-primary-300 leading-snug">
            <i className="ri-eye-line mr-1" aria-hidden="true"></i>
            Öğrencilerin mesajları <span className="font-semibold">yalnızca size</span> görünür.
          </p>
        </div>
      )}
      {!isHost && (
        <div className="mx-3 mt-3 px-3 py-2 rounded-lg bg-background-800 border border-background-700 shrink-0">
          <p className="text-[11px] text-background-400 leading-snug">
            <i className="ri-lock-line mr-1" aria-hidden="true"></i>
            Mesajlarınız <span className="font-semibold text-background-300">yalnızca öğretmeninize</span> iletilir.
          </p>
        </div>
      )}

      {/* Messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5">
        {messages.length === 0 ? (
          <p className="text-xs text-background-500 text-center mt-8">
            {isHost ? "Öğrencilerden mesaj bekleniyor…" : "Öğretmeninize mesaj gönderin."}
          </p>
        ) : (
          rendered.map(({ m, showHeader }) => (
            <div
              key={m.id}
              className={`group flex flex-col ${m.mine ? "items-end" : "items-start"}`}
              onMouseEnter={() => setHoverReaction(m.id)}
              onMouseLeave={() => setHoverReaction((p) => (p === m.id ? null : p))}
            >
              {showHeader && (
                <p
                  className={`text-[10px] mb-0.5 px-1 ${
                    m.mine ? "text-background-500" : "text-accent-400 font-semibold"
                  }`}
                >
                  {m.name}
                </p>
              )}
              <div
                className={`relative max-w-[85%] rounded-xl px-3 py-2 ${
                  m.mine
                    ? "bg-primary-500 text-white rounded-br-sm"
                    : "bg-background-700 text-background-100 rounded-bl-sm"
                }`}
              >
                {m.attachment?.kind === "image" && m.attachment.url && (
                  <button
                    type="button"
                    onClick={() => setPreview({ url: m.attachment!.url, name: m.attachment!.name })}
                    className="block mb-1.5 rounded-lg overflow-hidden cursor-zoom-in"
                    aria-label="Resmi büyüt"
                  >
                    <img
                      src={m.attachment.url}
                      alt={m.attachment.name}
                      className="max-w-[220px] max-h-[220px] object-cover rounded-lg"
                    />
                  </button>
                )}
                {m.attachment?.kind === "file" && m.attachment.url && (
                  <a
                    href={m.attachment.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={`flex items-center gap-2.5 mb-1.5 p-2.5 rounded-lg ${
                      m.mine ? "bg-white/15" : "bg-background-800"
                    } hover:opacity-90 transition-opacity`}
                  >
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-700 text-lg shrink-0">
                      <i className="ri-file-3-line" aria-hidden="true"></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs font-semibold truncate max-w-[140px]">
                        {m.attachment.name}
                      </span>
                      <span className={`block text-[10px] ${m.mine ? "text-white/70" : "text-background-500"}`}>
                        {formatFileSize(m.attachment.size)}
                      </span>
                    </span>
                    <i className="ri-download-2-line shrink-0" aria-hidden="true"></i>
                  </a>
                )}
                {m.text && <p className="text-sm leading-snug break-words">{m.text}</p>}
                <div
                  className={`flex items-center gap-1 mt-1 ${
                    m.mine ? "justify-end text-white/60" : "text-background-500"
                  }`}
                >
                  <span className="text-[10px]">{m.time}</span>
                  {m.reaction && <span className="text-xs leading-none ml-0.5">{m.reaction}</span>}
                </div>

                {/* Reaction bar on hover */}
                {hoverReaction === m.id && (
                  <div
                    className={`absolute -top-4 ${m.mine ? "left-2" : "right-2"} flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-background-800 border border-background-700 shadow-lg z-20`}
                  >
                    {QUICK_REACTIONS.map((e) => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => onReact(m.id, e)}
                        className="w-6 h-6 flex items-center justify-center text-base rounded-full hover:bg-background-700 hover:scale-110 transition-transform"
                        aria-label={`Tepki ${e}`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}

        {/* Typing indicator */}
        {typingLabel && (
          <div className="flex items-center gap-2 px-1 pt-1">
            <span className="flex gap-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-background-500 animate-bounce" style={{ animationDelay: "0ms" }}></span>
              <span className="w-1.5 h-1.5 rounded-full bg-background-500 animate-bounce" style={{ animationDelay: "150ms" }}></span>
              <span className="w-1.5 h-1.5 rounded-full bg-background-500 animate-bounce" style={{ animationDelay: "300ms" }}></span>
            </span>
            <span className="text-[11px] text-background-500">
              {typingLabel} yazıyor…
            </span>
          </div>
        )}
      </div>

      {/* Upload error */}
      {uploadError && (
        <div className="mx-3 mb-1 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 shrink-0">
          <p className="text-[11px] text-red-400">{uploadError}</p>
        </div>
      )}

      {/* Input */}
      <form onSubmit={submit} className="p-3 border-t border-background-700/50 shrink-0 relative">
        {showEmoji && <EmojiPicker onPick={pickEmoji} onClose={() => setShowEmoji(false)} />}

        {uploading && (
          <div className="absolute bottom-full left-3 right-3 mb-2 px-3 py-2 rounded-lg bg-background-800 border border-background-700 flex items-center gap-2 z-30">
            <i className="ri-loader-4-line text-sm text-primary-400 animate-spin" aria-hidden="true"></i>
            <span className="text-xs text-background-300 truncate flex-1">Yükleniyor: {uploadName}</span>
          </div>
        )}

        <div className="flex items-end gap-1.5">
          {/* Emoji toggle */}
          <button
            type="button"
            onClick={() => setShowEmoji((s) => !s)}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors shrink-0 ${
              showEmoji ? "bg-primary-500 text-white" : "bg-background-700 text-background-300 hover:bg-background-600"
            }`}
            aria-label="Emoji ekle"
            title="Emoji"
          >
            <i className="ri-emotion-happy-line text-lg" aria-hidden="true"></i>
          </button>

          {/* File/image attach */}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-700 text-background-300 hover:bg-background-600 transition-colors shrink-0"
            aria-label="Dosya veya resim ekle"
            title="Dosya / Resim"
          >
            <i className="ri-attachment-2 text-lg" aria-hidden="true"></i>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx,.txt,.zip"
            onChange={(e) => handleFilePick(e.target.files)}
            className="hidden"
          />

          <textarea
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (text.trim()) {
                  onSend(text.trim());
                  setText("");
                  setShowEmoji(false);
                  stopTyping();
                }
              }
            }}
            placeholder={isHost ? "Öğrenciye yaz…" : "Öğretmene mesaj yaz…"}
            rows={1}
            className="flex-1 max-h-24 min-h-10 px-3 py-2.5 rounded-lg bg-background-700 text-sm text-background-100 placeholder:text-background-500 focus:outline-none focus:ring-1 focus:ring-primary-400 resize-none"
          />
          <button
            type="submit"
            disabled={!text.trim()}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-40 transition-colors shrink-0"
            aria-label="Gönder"
          >
            <i className="ri-send-plane-fill" aria-hidden="true"></i>
          </button>
        </div>
      </form>

      {/* Image preview modal */}
      {preview && (
        <ImagePreview url={preview.url} name={preview.name} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}